import { ApiClientBase } from '@@/api/api';
import { ApiResult } from '@@/types/ApiResult';

export type FreshdeskHttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * A single proxied request to Freshdesk.
 * `path`: Freshdesk resource path (e.g. `/tickets`)
 * `method`: HTTP verb
 * `query`: string or object to be turned into a querystring
 * `body`: JSON string/object or FormData; GET requests ignore body
 * `headers`: additional headers to merge with auth headers
 */
export interface FreshdeskProxyRequest {
  path: string;
  method: FreshdeskHttpMethod;
  query?: string | Record<string, string | number | boolean | undefined>;
  body?: BodyInit | string;
  headers?: Record<string, string>;
}

export class FreshdeskClient extends ApiClientBase {
  private normalizeQuery(query?: FreshdeskProxyRequest['query']): string {
    if (!query) return '';
    if (typeof query === 'string') return query.startsWith('?') ? query : `?${query}`;
    const search = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') search.append(key, String(value));
    });
    const qs = search.toString();
    return qs ? `?${qs}` : '';
  }

  async proxy<T = unknown>(request: FreshdeskProxyRequest): Promise<ApiResult<T> | T> {
    const { path, method, body, headers: extraHeaders } = request;
    const extraQuery = this.normalizeQuery(request.query);

    // Build final URL with instanceId=global appended last
    const hasLeadingSlash = path.startsWith('/');
    const [rawPath, pathQueryString] = path.split('?');
    const cleanPath = hasLeadingSlash ? rawPath : `/${rawPath}`;

    const qp = new URLSearchParams();
    if (pathQueryString) {
      new URLSearchParams(pathQueryString).forEach((value, key) => {
        if (key.toLowerCase() !== 'instanceid') qp.append(key, value);
      });
    }
    if (extraQuery) {
      const q = extraQuery.startsWith('?') ? extraQuery.slice(1) : extraQuery;
      new URLSearchParams(q).forEach((value, key) => {
        if (key.toLowerCase() !== 'instanceid') qp.append(key, value);
      });
    }
    const paramsWithoutInstanceId = qp.toString();
    const finalQuery = paramsWithoutInstanceId ? `${paramsWithoutInstanceId}&instanceId=global` : 'instanceId=global';

    const endpoint = this.getFreshdeskApiUrl(`${cleanPath}?${finalQuery}`);

    const authHeaders = await this.getHeaders();
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const headers: Record<string, string> = { ...(authHeaders || {}), ...(extraHeaders || {}) };
    // Let the browser set the correct boundary when sending FormData
    if (isFormData && 'content-type' in headers) delete headers['content-type'];

    const response = await fetch(endpoint, {
      method,
      headers,
      body: method === 'GET' ? undefined : body != null ? body : undefined
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'Freshdesk proxy request failed');
    }

    const json = await response.json().catch(() => ({}));
    if (json && typeof json === 'object' && 'data' in json) return json as ApiResult<T>;
    return json as T;
  }
}
